import { useMutation, useQueryClient } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"

interface UpdateContractStartDateParams {
  personId: string
  contractStartDate: string // ISO 8601 date string (YYYY-MM-DD)
  applicationId?: string
}

/**
 * Custom hook for updating a person's contract start date
 * Calls the update-person-contract-start-date Edge Function which:
 * 1. Updates the contract_start_date in Supabase
 * 2. Syncs the changes to Airtable (if airtable_record_id exists)
 */
export function useUpdateContractStartDate() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async ({
      personId,
      contractStartDate,
      applicationId,
    }: UpdateContractStartDateParams) => {
      const { data, error } = await supabase.functions.invoke("update-person-contract-start-date", {
        body: {
          personId,
          contractStartDate,
          applicationId,
        },
      })

      if (error) {
        throw new Error(`Failed to update contract start date: ${error.message}`)
      }

      if (!data.success) {
        throw new Error(data.error || "Failed to update contract start date")
      }

      return data.data
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: ["people"] })
      queryClient.invalidateQueries({ queryKey: ["person", variables.personId] })
    },
  })

  return {
    updateContractStartDate: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}
